import { useState } from 'react';
import { KeyRound, Trash2, UserX } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useDeleteLacUserMutation,
  useResetLacUserPasswordMutation,
  useSetLacUserEnabledMutation,
} from '../../features/lac/mutations/useLacUserMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { LacUser } from '../../types/lac';
import { ResetUserPasswordDialog } from '../../features/user/components/ResetUserPasswordDialog';
import {
  getUserProfileBackPath,
  resolveLacTabPath,
} from '../../routes/lacPaths';

export type LacUserProfileAdminBarProps = {
  profile: LacUser;
  tenantName: string;
  userRef: string;
};

export function LacUserProfileAdminBar({
  profile,
  tenantName,
  userRef,
}: LacUserProfileAdminBarProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = getUserProfileBackPath(location.state, resolveLacTabPath());
  const deleteUserMutation = useDeleteLacUserMutation();
  const resetPasswordMutation = useResetLacUserPasswordMutation();
  const setEnabledMutation = useSetLacUserEnabledMutation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  const isBusy =
    deleteUserMutation.isPending ||
    resetPasswordMutation.isPending ||
    setEnabledMutation.isPending;
  const isUserEnabled = profile.enabled !== false;

  const handleToggleEnabled = () => {
    const nextEnabled = !isUserEnabled;
    setEnabledMutation.mutate(
      {
        tenantName,
        userRef,
        profile,
        enabled: nextEnabled,
      },
      {
        onSuccess: () => {
          toast.success({
            title: nextEnabled
              ? translate('lac.users.profile.enableSuccessTitle')
              : translate('lac.users.profile.disableSuccessTitle'),
            description: nextEnabled
              ? translate('lac.users.profile.enableSuccessDescription', {
                  username: profile.name,
                })
              : translate('lac.users.profile.disableSuccessDescription', {
                  username: profile.name,
                }),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            nextEnabled
              ? translate('lac.users.profile.enableErrorTitle')
              : translate('lac.users.profile.disableErrorTitle'),
            nextEnabled
              ? translate('lac.users.profile.enableErrorDescription')
              : translate('lac.users.profile.disableErrorDescription'),
          );
        },
      },
    );
  };

  const handleResetPassword = (password: string, temporary: boolean) => {
    resetPasswordMutation.mutate(
      {
        tenantName,
        userRef,
        password,
        temporary,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('lac.users.profile.resetPasswordSuccessTitle'),
            description: translate(
              'lac.users.profile.resetPasswordSuccessDescription',
              { username: profile.name },
            ),
          });
          setIsResetPasswordOpen(false);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('lac.users.profile.resetPasswordErrorTitle'),
          );
        },
      },
    );
  };

  const handleDeleteUser = () => {
    deleteUserMutation.mutate(
      { tenantName, userRef },
      {
        onSuccess: () => {
          toast.success({
            title: translate('lac.users.profile.deleteSuccessTitle'),
            description: translate(
              'lac.users.profile.deleteSuccessDescription',
              {
                username: profile.name,
              },
            ),
          });
          setIsDeleteConfirmOpen(false);
          navigate(backPath);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('lac.users.profile.deleteErrorTitle'),
            translate('lac.users.profile.deleteErrorDescription'),
          );
        },
        onSettled: () => {
          setIsDeleteConfirmOpen(false);
        },
      },
    );
  };

  return (
    <>
      <div className="lac-user-profile__admin-actions">
        <LiksButton
          type="button"
          variant="secondary"
          disabled={isBusy}
          onClick={handleToggleEnabled}
        >
          <UserX strokeWidth={2} aria-hidden />
          {isUserEnabled
            ? translate('lac.users.profile.disable')
            : translate('lac.users.profile.enable')}
        </LiksButton>
        <LiksButton
          type="button"
          variant="secondary"
          disabled={isBusy}
          onClick={() => setIsResetPasswordOpen(true)}
        >
          <KeyRound strokeWidth={2} aria-hidden />
          {translate('lac.users.profile.resetPassword')}
        </LiksButton>
        <LiksButton
          type="button"
          variant="secondary"
          className="lac-user-profile__delete-button"
          disabled={isBusy}
          onClick={() => setIsDeleteConfirmOpen(true)}
        >
          <Trash2 strokeWidth={2} aria-hidden />
          {translate('lac.users.profile.delete')}
        </LiksButton>
      </div>

      {isResetPasswordOpen && (
        <ResetUserPasswordDialog
          onReset={handleResetPassword}
          onClose={() => setIsResetPasswordOpen(false)}
          isSubmitting={resetPasswordMutation.isPending}
        />
      )}

      <LiksConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title={translate('lac.users.profile.deleteConfirmTitle')}
        message={translate('lac.users.profile.deleteConfirmMessage', {
          username: profile.name,
        })}
        confirmLabel={translate('lac.users.profile.deleteConfirm')}
        cancelLabel={translate('lac.users.profile.deleteCancel')}
        onConfirm={handleDeleteUser}
        onCancel={() => {
          if (!deleteUserMutation.isPending) {
            setIsDeleteConfirmOpen(false);
          }
        }}
      />
    </>
  );
}
